import { useCallback, useState } from 'react';
import { componentToCanvasAPI } from '../services/api';

/**
 * Hook to handle AG Component updates based on job progress
 * Manages toast notifications (via componentUpdateItems) and video reloading when rendering completes
 * @param {Function} stopPlaybackLoop - Required function to stop playback loop before reloading video
 */
export const useAgComponentUpdates = (stopPlaybackLoop) => {
  // Local state for component update toast notifications
  const [componentUpdateItems, setComponentUpdateItems] = useState([]);
  
  // Track which elements are currently reloading their source
  const [reloadingElements, setReloadingElements] = useState(new Set());

  /**
   * Add or update a component update item
   */
  const addComponentUpdate = useCallback((item) => {
    setComponentUpdateItems(prev => {
      const existingIndex = prev.findIndex(i => i.id === item.id);
      if (existingIndex >= 0) {
        // Update existing item
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], ...item, timestamp: Date.now() };
        return updated;
      } else {
        // Add new item
        return [...prev, { ...item, timestamp: Date.now() }];
      }
    });
  }, []);

  /**
   * Remove a component update item
   */
  const removeComponentUpdate = useCallback((id) => {
    setComponentUpdateItems(prev => prev.filter(item => item.id !== id));
  }, []);

  /**
   * Determines the display status and message based on job status
   */
  const getDisplayStatus = (jobStatus, jobProgress) => {
    const progressValue = parseFloat(jobProgress) || 0;

    if (jobStatus === 'finished' && progressValue >= 100) {
      return { status: 'success', message: 'Updated' };
    }

    if (jobStatus === 'error') {
      return { status: 'error', message: 'Update failed' };
    }

    // Loading states
    if (jobStatus === 'waiting' || jobStatus === 'queued') {
      return { status: 'loading', message: 'Waking up...' };
    }

    if (jobStatus === 'started' || jobStatus === 'render:dorender') {
      return {
        status: 'loading',
        message: progressValue > 0 ? `Rendering ${Math.round(progressValue)}%` : 'Rendering...'
      };
    }

    return { status: 'loading', message: 'Processing...' };
  };

  /**
   * Updates toast notification based on job status
   */
  const updateVisualState = useCallback((element, jobStatus, jobProgress) => {
    if (!element) return;

    const elementName = element.componentData?.name || element.name || 'Component';
    const progressValue = parseFloat(jobProgress) || 0;
    const { status } = getDisplayStatus(jobStatus, jobProgress);

    switch (jobStatus) {
      case 'waiting':
      case 'queued':
      case 'started':
      case 'render:dorender':
        console.log('Job progress update:', jobProgress, 'Status:', jobStatus);

        // Add/update toast notification
        addComponentUpdate({
          id: element.id,
          name: elementName,
          type: 'component',
          status,
          progress: progressValue,
          jobStatus
        });
        break;

      case 'finished':
        if (progressValue >= 100 || jobProgress === '100%') {
          // Update toast to show success
          addComponentUpdate({
            id: element.id,
            name: elementName,
            type: 'component',
            status: 'success',
            progress: 100,
            jobStatus
          });

          // Reload the video source
          reloadVideoSource(element);

          // Auto-remove toast after 3 seconds
          setTimeout(() => {
            removeComponentUpdate(element.id);
          }, 3000);
        }
        break;

      case 'error':
        // Update toast to show error
        addComponentUpdate({
          id: element.id,
          name: elementName,
          type: 'component',
          status: 'error',
          progress: 0,
          jobStatus
        });

        // Auto-remove toast after 5 seconds for errors
        setTimeout(() => {
          removeComponentUpdate(element.id);
        }, 5000);
        break;

      default:
        // For unknown states, show processing
        addComponentUpdate({
          id: element.id,
          name: elementName,
          type: 'component',
          status: 'loading',
          progress: progressValue,
          jobStatus
        });
        break;
    }
  }, [addComponentUpdate, removeComponentUpdate]);

  /**
   * Reloads the video source when rendering is complete
   * Returns a promise that resolves when the video is ready to use
   */
  const reloadVideoSource = async (element) => {
    if (!element.pixiSprite?.videoElement) return;

    // Mark element as reloading - this prevents canvasStore from updating the video
    setReloadingElements(prev => new Set(prev).add(element.id));

    try {
      // Stop playback to prevent PixiJS video source infinite loop
      stopPlaybackLoop();

      // Get the updated video URL
      let videoUrl = element.componentData?.url || element.componentData?.preview;

      if (element.componentData) {
        try {
          const response = await componentToCanvasAPI.getComponentRenderPath(element.id);
          videoUrl = response.data.lowResRenderPath;
        } catch (error) {
          console.error('Error fetching render path:', error);
        }
      }

      if (!videoUrl) return;

      const sprite = element.pixiSprite;
      const video = sprite.videoElement;

      // Store current time to restore after reload
      const currentTime = video.currentTime || 0;

      // Pause video before changing source
      video.pause();

      // Update the src - sprite stays visible, isReloading flag prevents interference
      video.src = `${videoUrl}?t=${Date.now()}`;

      // Force the video to load immediately
      video.load();

      // Wait for video to be ready
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Video load timeout')), 5000);
        
        const onCanPlay = () => {
          clearTimeout(timeout);
          video.removeEventListener('canplay', onCanPlay);
          video.removeEventListener('error', onError);
          resolve();
        };
        
        const onError = (err) => {
          clearTimeout(timeout);
          video.removeEventListener('canplay', onCanPlay);
          video.removeEventListener('error', onError);
          reject(err);
        };
        
        video.addEventListener('canplay', onCanPlay);
        video.addEventListener('error', onError);
      });

      // Seek to the current timeline position (or start)
      video.currentTime = currentTime;

      // Briefly play the video to force PixiJS to update the texture, then pause
      // This is necessary because PixiJS only updates video textures when playing
      await video.play();
      
      // Wait a tiny bit for the texture to update
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Pause at the current position
      video.pause();

      console.log('🔄✅ Updated video source and refreshed texture for', element.id);
    } catch (error) {
      console.error('Error updating video source:', error);
    } finally {
      // Mark element as no longer reloading
      setReloadingElements(prev => {
        const next = new Set(prev);
        next.delete(element.id);
        return next;
      });
    }
  };

  /**
   * Main update function to be called when job status changes
   */
  const handleAgComponentUpdate = useCallback((element, updates) => {
    if (!element || !updates) return;

    // Update visual state based on job status and progress
    if (updates.jobStatus !== undefined || updates.jobProgress !== undefined) {
      const status = updates.jobStatus || element.jobStatus;
      const progress = updates.jobProgress || element.jobProgress;
      updateVisualState(
        { ...element, ...updates },
        status,
        progress
      );
    }
  }, [updateVisualState]);

  /**
   * Cleanup function - removes all toast notifications
   */
  const cleanupAll = useCallback(() => {
    setComponentUpdateItems([]);
  }, []);

  /**
   * Check if an element is currently reloading its source
   */
  const isElementReloading = useCallback((elementId) => {
    return reloadingElements.has(elementId);
  }, [reloadingElements]);

  return {
    handleAgComponentUpdate,
    updateVisualState,
    reloadVideoSource,
    cleanupAll,
    componentUpdateItems, // Expose for use in CanvasPage
    isElementReloading, // Expose to check if element is reloading
  };
};

export default useAgComponentUpdates;